import React, { useEffect, useState } from "react";
import { Form, Input, Table, Typography, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";

export interface DeliverablesType {
  key: string;
  fileName: string;
  description: string;
}

interface DeliverablesTableProps {
  deliverablesData: any;
  handleDeliverables: (data: DeliverablesType[]) => void;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  record: DeliverablesType;
  index: number;
  form: any;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  record,
  index,
  children,
  form,
  ...restProps
}) => {
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          <Input />
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const DeliverablesTable: React.FC<DeliverablesTableProps> = ({
  deliverablesData,
  handleDeliverables,
}) => {
  const [form] = Form.useForm();
  const [data, setData] = useState<DeliverablesType[]>([]);
  const [editingKey, setEditingKey] = useState("");
  const [isNewRow, setIsNewRow] = useState(false);

  const isEditing = (record: DeliverablesType) => record.key === editingKey;

  const handleEdit = (record: DeliverablesType) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key);
  };

  useEffect(() => {
    if (deliverablesData && data.length === 0) {
      setData(
        deliverablesData.deliverables.map((item: any, index: any) => ({
          key: item.key || `existing-${index}`,
          fileName: item.fileName,
          description: item.description,
        })),
      );
    }
  }, [deliverablesData, data.length]);

  const handleCancel = (key: React.Key) => {
    if (isNewRow === true) {
      // Remove the last row if it's empty
      const newData = data.slice(0, -1);
      setData(newData);
    }
    setEditingKey("");
    setIsNewRow(false);
  };

  const handleAddNewButton = () => {
    if (!isNewRow) {
      const newKey = `new-${Date.now()}`; // unique key
      const newEntry: DeliverablesType = {
        key: newKey,
        fileName: "",
        description: "",
      };
      setData((prevData) => [...prevData, newEntry]);
      setIsNewRow(true);
      handleEdit(newEntry);
    }
  };

  const updateData = (newData: DeliverablesType[]) => {
    setData(newData);
    handleDeliverables(newData);
  };

  const handleRemoveButton = (key: React.Key) => {
    const newData = data.filter((item) => item.key !== key);
    setData(newData);
    updateData(newData);
    if (editingKey === key) {
      setEditingKey("");
      setIsNewRow(false);
    }
  };

  const handleSave = async (key: React.Key) => {
    try {
      const row = (await form.validateFields()) as DeliverablesType;
      const duplicate = data.some(
        (item) =>
          item.fileName.trim().toLowerCase() === row.fileName.trim().toLowerCase() &&
          item.key !== key
      );

      if (duplicate) {
        form.setFields([
          {
            name: "fileName",
            errors: ["A Deliverable already exists with the same Name"],
          },
        ]);
        return;
      }

      setData((prevData) => {
        const newData = [...prevData];
        const index = newData.findIndex((item) => key === item.key);
        if (index > -1) {
          const item = newData[index];
          newData.splice(index, 1, { ...item, ...row });
          updateData(newData);
          return newData;
        }
        return prevData;
      });
      setEditingKey("");
      setIsNewRow(false);
    } catch (errInfo) {
      return;
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "fileName",
      width: "30%",
      editable: true,
    },
    {
      title: "Description of Deliverable",
      dataIndex: "description",
      width: "50%",
      editable: true,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: DeliverablesType) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Typography.Link
              onClick={() => handleSave(record.key)}
              style={{ marginInlineEnd: 8 }}
            >
              Save
            </Typography.Link>
            <Typography.Link onClick={() => handleCancel(record.key)}>
              Cancel
            </Typography.Link>
          </span>
        ) : (
          <span>
            <Typography.Link
              style={{ marginInlineEnd: 8 }}
              disabled={editingKey !== ""}
              onClick={() => handleRemoveButton(record.key)}
            >
              Remove
            </Typography.Link>
            <Typography.Link
              disabled={editingKey !== ""}
              onClick={() => handleEdit(record)}
            >
              Edit
            </Typography.Link>
          </span>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: DeliverablesType) => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        form,
      }),
    };
  });

  return (
    <Form form={form} component={false}>
      <Table<DeliverablesType>
        components={{
          body: { cell: EditableCell },
        }}
        dataSource={data}
        columns={mergedColumns}
        rowClassName="editable-row"
        pagination={false}
        locale={{ emptyText: null }}
        tableLayout="fixed"
        style={{ width: "100%" }}
      />
      <div className="contact-add-button" id="add-button">
        <Form.Item>
          <Button
            type="dashed"
            onClick={handleAddNewButton}
            style={{ fontWeight: "bold" }}
            block
            icon={<PlusOutlined />}
          >
            Add New Deliverable
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default DeliverablesTable;
